import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import shortid from 'shortid';
import './styles/dropdown.scss';

export const variantPropMap = {
  tonal: {
    variant: 'filled',
    className: 'dropdown--tonal'
  },
  filter: {
    variant: 'outlined',
    className: 'dropdown--filter'
  }
};

/** @component
 * @param {Object} props
 * @param {String} props.fieldId - generate id with shortid, or other suitable method and pass in as prop if needed
 */
export default function Dropdown({
  items,
  label = '',
  variant,
  id = '',
  fieldId,
  onSelect,
  value,
  helperText = '',
  disabled,
  inputProps,
  className: propClassName,
  error,
  showErrorMessage = true,
  inlineLabel,
  ...props
}) {
  fieldId = fieldId ?? shortid.generate();
  const MUIDropdownProps = variant && variantPropMap[variant];
  const { variant: dropdownVariant, className } = MUIDropdownProps || {};

  return (
    <FormControl
      variant={dropdownVariant || 'outlined'}
      fullWidth
      className={`dropdown ${className || ''} ${propClassName || ''}`}
      error={!!error}
    >
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        IconComponent={KeyboardArrowDownIcon}
        className='dropdown__menu'
        id={id || fieldId}
        {...(label && { label: label })}
        onChange={onSelect}
        disabled={disabled}
        {...(inlineLabel && {
          displayEmpty: true,
          renderValue: (selected) => {
            if (
              selected === null ||
              selected === undefined ||
              selected === ''
            ) {
              return (
                <span className='dropdown__placeholder'>{inlineLabel}</span>
              );
            }
            const item = items.find((item) => item.value === selected);
            return item?.value === undefined ? '' : item.value;
          }
        })}
        value={value}
        MenuProps={{
          className: 'dropdown__menu',
          transitionDuration: 0,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          }
        }}
        inputProps={inputProps}
        {...(props || {})}
      >
        <MenuItem
          value={null}
          className='dropdown__item__hidden'
        />
        {items &&
          items.map((item, index) => (
            <MenuItem
              className='dropdown__item'
              key={(id || fieldId) + (item.value || index)}
              value={item.value}
              disableRipple
              disableGutters
            >
              {item.label}
            </MenuItem>
          ))}
      </Select>
      {showErrorMessage && error && <FormHelperText>{error}</FormHelperText>}
      {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

Dropdown.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string,
  fieldId: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  helperText: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  inputProps: PropTypes.object,
  className: PropTypes.string,
  error: PropTypes.string,
  variant: PropTypes.string,
  inlineLabel: PropTypes.string,
  showErrorMessage: PropTypes.bool
};
